import { format } from 'date-fns';
import {
  ArrowDown,
  ArrowDownToLine,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  MoreVertical,
  Pin,
  PinOff,
  RotateCcw,
  Search,
  Wand2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { HelpSupportButton } from '@/components/help-support';
import type { ColumnDefinition } from '@/components/shared/column-config-dialog';
import { TransactionTypeBadge } from '@/components/transactions/transaction-type-badge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { CurrencyInput } from '@/components/ui/currency-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UniqueTableNamesForColumnConfiguration } from '@/config/unique-table-names-for-column-configuration';
import { useCompany } from '@/contexts/CompanyContext';
import { useAllocationWorkspaceData } from '@/hooks/useAllocationData';
import { ApiError } from '@/hooks/useApi';
import { useCustomerPromises } from '@/hooks/useCustomerPromises';
import { useScreenColumnPreferences } from '@/hooks/useScreenColumnPreferences';
import { useSessionSortPreference } from '@/hooks/useSessionSortPreference';
import { cn } from '@/lib/utils';
import type { AllocationItemRequest } from '@/types/api/allocations';
import type { Transaction } from '@/types/payments';
import type { TransactionType } from '@/types/transaction';
import {
  AutoAllocatePromisesModal,
  hasOutstandingPromises,
  type PromiseAllocationResult,
} from './auto-allocate-promises-modal';

export interface AllocationTransaction {
  id: number;
  transactionNumber: string;
  transactionType: string;
  description?: string;
  customerId: number;
  customerName: string;
  transactionDate: Date;
  dueDate: Date;
  originalAmount: number;
  currentBalance: number;
  currency: string;
  /** Home-currency equivalent of originalAmount */
  homeCurrencyAmount?: number;
  /** Home-currency equivalent of currentBalance */
  homeCurrencyBalance?: number;

  daysOverdue: number;
  onDispute: boolean;
  hasDispute: boolean;
  isPromised?: boolean;
}

export interface AllocationRow {
  transaction: AllocationTransaction;
  allocatedAmount: number;
  isSelected: boolean;
}

type SortField =
  | 'transactionNumber'
  | 'transactionType'
  | 'currency'
  | 'transactionDate'
  | 'dueDate'
  | 'originalAmount'
  | 'currentBalance'
  | 'allocatedAmount';
type SortDirection = 'asc' | 'desc' | null;

function mapTransactionToAllocationTransaction(transaction: Transaction): AllocationTransaction {
  return {
    id: transaction.id,
    transactionNumber: transaction.transactionNumber,
    transactionType: transaction.transactionType,
    description: transaction.description,
    customerId: transaction.customerId,
    customerName: transaction.customerName,
    transactionDate: transaction.transactionDate,
    dueDate: transaction.dueDate,
    originalAmount: transaction.originalAmount,
    currentBalance: transaction.currentBalance,
    currency: transaction.currency,
    homeCurrencyAmount: transaction.homeCurrencyAmount,
    homeCurrencyBalance: transaction.homeCurrencyBalance,

    daysOverdue: transaction.daysOverdue,
    onDispute: transaction.onDispute,
    hasDispute: transaction.hasDispute ?? false,
    isPromised: transaction.isPromised,
  };
}

function getSortValue(row: AllocationRow, field: SortField): string | number | Date {
  if (field === 'allocatedAmount') return row.allocatedAmount;
  return row.transaction[field];
}

function sortAllocationRows(rows: AllocationRow[], field: SortField, direction: 'asc' | 'desc'): AllocationRow[] {
  return [...rows].sort((a, b) => {
    let aValue = getSortValue(a, field);
    let bValue = getSortValue(b, field);

    // Handle date sorting
    if (field === 'transactionDate' || field === 'dueDate') {
      aValue = new Date(aValue).getTime();
      bValue = new Date(bValue).getTime();
    }

    // Handle numeric sorting
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return direction === 'asc' ? aValue - bValue : bValue - aValue;
    }

    // Handle string sorting
    const aStr = String(aValue).toLowerCase();
    const bStr = String(bValue).toLowerCase();
    return direction === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
  });
}

// ─── Column Config ────────────────────────────────────────────────────────────

const DEFAULT_ALLOCATION_COLUMNS = [
  'checkbox',
  'transactionNumber',
  'transactionType',
  'description',
  'currency',
  'transactionDate',
  'dueDate',
  'originalAmount',
  'currentBalance',
  'allocatedAmount',
];

const DEFAULT_ALLOCATION_COLUMN_DEFS: ColumnDefinition[] = DEFAULT_ALLOCATION_COLUMNS.map((id, index) => ({
  id,
  label: id,
  visible: true,
  order: index,
}));

const ALLOC_COL_CONFIG: Record<string, { label: string; sortField?: string; align?: 'right' }> = {
  checkbox: { label: '' },
  transactionNumber: { label: 'Reference Number', sortField: 'transactionNumber' },
  transactionType: { label: 'Type', sortField: 'transactionType' },
  description: { label: 'Description' },
  currency: { label: 'Currency', sortField: 'currency' },
  transactionDate: { label: 'Date', sortField: 'transactionDate' },
  dueDate: { label: 'Due Date', sortField: 'dueDate' },
  originalAmount: { label: 'Gross', sortField: 'originalAmount', align: 'right' },
  currentBalance: { label: 'Outstanding', sortField: 'currentBalance', align: 'right' },
  allocatedAmount: { label: 'Allocated Amount', sortField: 'allocatedAmount', align: 'right' },
};

export default function AllocationWorkspace() {
  const { receiptId } = useParams<{ receiptId: string }>();
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  const homeCurrency = selectedCompany?.currency || 'GBP';

  // Use mode-aware hook for data fetching (respects demo/live toggle)
  const { receipt, transactions, isLoading, error, createAllocations, isSubmitting } = useAllocationWorkspaceData(
    receiptId ? Number(receiptId) : undefined,
    homeCurrency,
  );

  // Local state for allocation rows (derived from transactions)
  const [allocationRows, setAllocationRows] = useState<AllocationRow[]>([]);

  // UI state
  const [searchQuery, setSearchQuery] = useState('');

  const [currencyFilter, setCurrencyFilter] = useState<string>('receipt');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showPromisesModal, setShowPromisesModal] = useState(false);

  // Outstanding promises (with linked invoices) for the receipt's customer, used by the
  // Auto-Allocate Promises review modal.
  const {
    promises: customerPromises,
    isLoading: isLoadingPromises,
    error: promisesError,
    refetch: refetchPromises,
  } = useCustomerPromises(receipt?.customerId ? String(receipt.customerId) : undefined, { pageSize: 500 });
  const [allocationDate, setAllocationDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));

  // Sorting state (persisted per screen for the user session)
  const [{ column: sortField, direction: sortDirection }, setSort] = useSessionSortPreference<
    SortField | null,
    SortDirection
  >(UniqueTableNamesForColumnConfiguration.AllocationWorkspace, null, null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Column interactions (persisted)
  const {
    columnOrder: persistedColumnOrder,
    setColumnOrder,
    columnWidths,
    setColumnWidths,
    pinnedLeft,
    pinnedRight,
    setPinned,
    resetColumnOrder,
    resetColumnWidths,
  } = useScreenColumnPreferences(
    UniqueTableNamesForColumnConfiguration.AllocationWorkspace,
    DEFAULT_ALLOCATION_COLUMN_DEFS,
  );
  const columnOrder = persistedColumnOrder.length > 0 ? persistedColumnOrder : DEFAULT_ALLOCATION_COLUMNS;
  const resizingRef = useRef<{ columnId: string; startX: number; startWidth: number } | null>(null);
  const dragColRef = useRef<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<string | null>(null);

  // Initialize allocation rows when transactions change
  useEffect(() => {
    if (transactions.length > 0) {
      setAllocationRows(
        transactions.map((t) => ({
          transaction: mapTransactionToAllocationTransaction(t),
          allocatedAmount: 0,
          isSelected: false,
        })),
      );
    }
  }, [transactions]);

  // Format currency — uses the supplied currency code (falls back to receipt currency, then GBP)
  const formatCurrency = (amount: number, currency?: string) => {
    const resolvedCurrency = currency ?? receipt?.currency ?? 'GBP';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: resolvedCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Export filtered rows to Excel (CSV download)
  const handleExportToExcel = () => {
    const isHome = currencyFilter === 'home';
    const currency = isHome ? homeCurrency : (receipt?.currency ?? homeCurrency);

    const headers = [
      'Reference Number',
      'Type',
      'Description',
      `Currency`,
      'Date',
      'Due Date',
      `Gross (${currency})`,
      `Outstanding (${currency})`,
      `Allocated Amount (${currency})`,
    ];

    const rows = filteredRows.map((row) => [
      row.transaction.transactionNumber,
      row.transaction.transactionType,
      row.transaction.description ?? '',
      row.transaction.currency,
      format(new Date(row.transaction.transactionDate), 'dd/MM/yyyy'),
      format(new Date(row.transaction.dueDate), 'dd/MM/yyyy'),
      isHome
        ? (row.transaction.homeCurrencyAmount ?? row.transaction.originalAmount).toFixed(2)
        : row.transaction.originalAmount.toFixed(2),
      isHome
        ? (row.transaction.homeCurrencyBalance ?? row.transaction.currentBalance).toFixed(2)
        : row.transaction.currentBalance.toFixed(2),
      row.allocatedAmount.toFixed(2),
    ]);

    const csvContent = [headers, ...rows]
      .map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `allocation_${receipt?.paymentReference ?? 'export'}_${format(new Date(), 'yyyyMMdd')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Calculate totals
  // When the Home Currency filter is active, use the home-currency equivalent receipt amount
  // for the right-hand panels; otherwise use the receipt's native currency amount.
  const displayReceivedAmount = useMemo(() => {
    if (currencyFilter === 'home' && receipt?.homeReceivedAmount != null) {
      return receipt.homeReceivedAmount;
    }
    return receipt?.receivedAmount ?? 0;
  }, [currencyFilter, receipt]);

  // Outstanding (unapplied) amount in the current display currency.
  // This is the actual amount available for new allocations.
  const displayOutstandingAmount = useMemo(() => {
    if (currencyFilter === 'home' && receipt?.homeUnappliedAmount != null) {
      return receipt.homeUnappliedAmount;
    }
    return receipt?.unappliedAmount ?? 0;
  }, [currencyFilter, receipt]);

  const displayPanelCurrency = useMemo(() => {
    return currencyFilter === 'home' ? homeCurrency : (receipt?.currency ?? homeCurrency);
  }, [currencyFilter, receipt, homeCurrency]);

  const totalAllocated = useMemo(() => {
    // Allocated amounts are always stored in the current display currency,
    // so just sum them directly (no rate conversion needed).
    return allocationRows.reduce((sum, row) => sum + row.allocatedAmount, 0);
  }, [allocationRows]);

  const remainingAmount = useMemo(() => {
    return displayOutstandingAmount - totalAllocated;
  }, [displayOutstandingAmount, totalAllocated]);

  const allocationProgress = useMemo(() => {
    if (!displayOutstandingAmount) return 0;
    return Math.min((totalAllocated / displayOutstandingAmount) * 100, 100);
  }, [totalAllocated, displayOutstandingAmount]);

  const selectedCount = useMemo(() => {
    return allocationRows.filter((row) => row.isSelected).length;
  }, [allocationRows]);

  // Determine if currency filter should be shown (only if receipt is not home currency)
  const showCurrencyFilter = useMemo(() => {
    return receipt?.currency && receipt.currency !== homeCurrency;
  }, [receipt, homeCurrency]);

  // Helper: get the outstanding balance in the currently displayed currency
  const getDisplayBalance = useCallback(
    (row: AllocationRow): number => {
      if (currencyFilter === 'home') {
        return row.transaction.homeCurrencyBalance ?? row.transaction.currentBalance;
      }
      return row.transaction.currentBalance;
    },
    [currencyFilter],
  );

  const totalSelectedAmount = useMemo(() => {
    return allocationRows.filter((row) => row.isSelected).reduce((sum, row) => sum + getDisplayBalance(row), 0);
  }, [allocationRows, getDisplayBalance]);

  // Clear allocations when currency filter changes (amounts are currency-specific)
  const prevCurrencyFilter = useRef(currencyFilter);
  useEffect(() => {
    if (prevCurrencyFilter.current !== currencyFilter) {
      prevCurrencyFilter.current = currencyFilter;
      setAllocationRows((prev) =>
        prev.map((row) => ({
          ...row,
          allocatedAmount: 0,
          isSelected: false,
        })),
      );
    }
  }, [currencyFilter]);

  // Filter and sort transactions
  const filteredRows = useMemo(() => {
    let filtered = allocationRows;

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter((row) =>
        row.transaction.transactionNumber.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    // Apply currency filter
    // Receipt mode: only show transactions in the receipt's currency.
    // Home mode: show all transactions across all currencies with amounts in base currency.
    if (currencyFilter === 'receipt') {
      filtered = filtered.filter((row) => row.transaction.currency === receipt?.currency);
    }

    // Apply status filter

    // Apply sorting
    if (sortField && sortDirection) {
      filtered = sortAllocationRows(filtered, sortField, sortDirection);
    }

    return filtered;
  }, [allocationRows, searchQuery, currencyFilter, sortField, sortDirection, receipt, homeCurrency]);

  // Reset to page 1 when filters/sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredRows.length]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, currentPage, rowsPerPage]);

  // Handle column header click for sorting
  // Resize handlers
  const [localColumnWidths, setLocalColumnWidths] = useState<Record<string, number>>(columnWidths);
  const localColumnWidthsRef = useRef(localColumnWidths);
  localColumnWidthsRef.current = localColumnWidths;

  useEffect(() => {
    setLocalColumnWidths(columnWidths);
  }, [columnWidths]);

  const startResize = (e: React.MouseEvent, columnId: string) => {
    e.preventDefault();
    const th = (e.target as HTMLElement).closest('th');
    const startWidth = th?.offsetWidth ?? 100;
    resizingRef.current = { columnId, startX: e.clientX, startWidth };
  };

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!resizingRef.current) return;
      const { columnId, startX, startWidth } = resizingRef.current;
      const newWidth = Math.max(60, startWidth + (e.clientX - startX));
      setLocalColumnWidths((prev) => ({ ...prev, [columnId]: newWidth }));
    };
    const onMouseUp = () => {
      if (resizingRef.current) {
        setColumnWidths(localColumnWidthsRef.current);
      }
      resizingRef.current = null;
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [setColumnWidths]);

  // Drag handlers
  const handleDragStart = (columnId: string) => {
    dragColRef.current = columnId;
  };
  const handleDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    setDragOverCol(columnId);
  };
  const handleDrop = (targetId: string) => {
    const sourceId = dragColRef.current;
    if (!sourceId || sourceId === targetId) {
      setDragOverCol(null);
      return;
    }
    const next = [...columnOrder];
    const from = next.indexOf(sourceId);
    const to = next.indexOf(targetId);
    if (from !== -1 && to !== -1) {
      next.splice(from, 1);
      next.splice(to, 0, sourceId);
      setColumnOrder(next);
    }
    setDragOverCol(null);
    dragColRef.current = null;
  };
  const handleDragEnd = () => {
    setDragOverCol(null);
    dragColRef.current = null;
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSort(field, 'desc');
      } else if (sortDirection === 'desc') {
        setSort(null, null);
      }
    } else {
      setSort(field, 'asc');
    }
  };

  // Render sort icon
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field)
      return (
        <span className="text-gray-500">
          <ArrowUpDown className="h-4 w-4" strokeWidth={2.5} />
        </span>
      );
    if (sortDirection === 'asc')
      return (
        <span className="text-gray-500">
          <ArrowUp className="h-4 w-4" strokeWidth={2.5} />
        </span>
      );
    return (
      <span className="text-gray-500">
        <ArrowDown className="h-4 w-4" strokeWidth={2.5} />
      </span>
    );
  };

  // Column header renderer
  const renderColHeader = (columnId: string) => {
    const cfg = ALLOC_COL_CONFIG[columnId];
    if (!cfg) return null;
    const isPinnedL = pinnedLeft.includes(columnId);
    const isPinnedR = pinnedRight.includes(columnId);
    const width = localColumnWidths[columnId];
    const isDragOver = dragOverCol === columnId;

    // Checkbox column — no label, no dropdown, no resize
    if (columnId === 'checkbox') {
      return (
        <th
          key="checkbox"
          className="w-12 px-4 bg-white font-bold text-gray-700 select-none border-r border-gray-200"
          style={width ? { width, minWidth: width } : undefined}
        />
      );
    }

    const label = cfg.sortField ? (
      <button
        className={cn(
          'flex items-center gap-1 hover:text-gray-900 transition-colors',
          cfg.align === 'right' && 'justify-end w-full',
        )}
        onClick={() => handleSort(cfg.sortField as SortField)}
      >
        {cfg.label}
        {renderSortIcon(cfg.sortField as SortField)}
      </button>
    ) : (
      <span className={cn(cfg.align === 'right' && 'block text-right')}>{cfg.label}</span>
    );

    return (
      <th
        key={columnId}
        draggable
        onDragStart={() => handleDragStart(columnId)}
        onDragOver={(e) => handleDragOver(e, columnId)}
        onDrop={() => handleDrop(columnId)}
        onDragEnd={handleDragEnd}
        className={cn(
          'relative h-10 px-4 bg-white font-bold text-gray-700 select-none whitespace-nowrap border-r border-gray-200 last:border-r-0 text-left',
          cfg.align === 'right' && 'text-right',
          isPinnedL && 'sticky left-0 z-20 shadow-[2px_0_4px_rgba(0,0,0,0.06)]',
          isPinnedR && 'sticky right-0 z-20 shadow-[-2px_0_4px_rgba(0,0,0,0.06)]',
          isDragOver && 'bg-blue-50 border-l-2 border-l-blue-400',
        )}
        style={width ? { width, minWidth: width } : undefined}
      >
        {/* Label + sort button */}
        <div className="pr-5">{label}</div>

        {/* ⋮ dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="absolute right-5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600">
              <MoreVertical className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {!isPinnedL && !isPinnedR ? (
              <>
                <DropdownMenuItem
                  onClick={() =>
                    setPinned(
                      [...pinnedLeft, columnId],
                      pinnedRight.filter((x) => x !== columnId),
                    )
                  }
                >
                  <Pin className="mr-2 h-4 w-4" /> Pin to Left
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    setPinned(
                      pinnedLeft.filter((x) => x !== columnId),
                      [...pinnedRight, columnId],
                    )
                  }
                >
                  <PinOff className="mr-2 h-4 w-4" /> Pin to Right
                </DropdownMenuItem>
              </>
            ) : (
              <DropdownMenuItem
                onClick={() =>
                  setPinned(
                    pinnedLeft.filter((x) => x !== columnId),
                    pinnedRight.filter((x) => x !== columnId),
                  )
                }
              >
                <PinOff className="mr-2 h-4 w-4" /> Unpin Column
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => resetColumnWidths()}>
              <RotateCcw className="mr-2 h-4 w-4" /> Reset Column Widths
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => resetColumnOrder()}>
              <RotateCcw className="mr-2 h-4 w-4" /> Reset Column Order
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Resize handle */}
        <div
          className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 active:bg-blue-500"
          onMouseDown={(e) => startResize(e, columnId)}
        />
      </th>
    );
  };

  // Handle allocation amount change
  const handleAllocationChange = (index: number, value: number | null) => {
    const numValue = value ?? 0;
    const row = paginatedRows[index];
    const originalIndex = allocationRows.findIndex((r) => r.transaction.id === row.transaction.id);

    const outstanding = getDisplayBalance(row);
    const isNegativeTransaction = outstanding < 0;

    // Validation: Cannot exceed transaction outstanding (using display currency)
    if (isNegativeTransaction) {
      if (numValue < outstanding) {
        toast.error('Cannot exceed transaction outstanding amount');
        return;
      }
    } else {
      if (numValue > outstanding) {
        toast.error('Cannot exceed transaction outstanding amount');
        return;
      }
    }

    // Validation: Cannot exceed remaining receipt amount (using display currency)
    const otherAllocations = allocationRows.reduce((sum, r, i) => {
      if (i === originalIndex) return sum;
      return sum + r.allocatedAmount;
    }, 0);

    const positiveAllocations = otherAllocations + Math.max(0, numValue);
    if (!isNegativeTransaction && positiveAllocations > displayOutstandingAmount) {
      toast.error('Cannot exceed receipt amount');
      return;
    }

    // Update allocation
    const newRows = [...allocationRows];
    newRows[originalIndex] = {
      ...newRows[originalIndex],
      allocatedAmount: numValue,
      isSelected: numValue !== 0 ? true : newRows[originalIndex].isSelected,
    };
    setAllocationRows(newRows);
  };

  // Handle checkbox toggle
  const handleCheckboxToggle = (index: number, checked: boolean) => {
    const row = paginatedRows[index];
    const originalIndex = allocationRows.findIndex((r) => r.transaction.id === row.transaction.id);

    if (checked) {
      const currentAllocated = allocationRows[originalIndex].allocatedAmount;
      const outstandingAmount = getDisplayBalance(row);
      const isNegativeTransaction = outstandingAmount < 0;

      // Calculate how much we can allocate (using display currency)
      const otherAllocations = allocationRows.reduce((sum, r, i) => {
        if (i === originalIndex) return sum;
        return sum + r.allocatedAmount;
      }, 0);

      const availableAmount = displayOutstandingAmount - otherAllocations;

      let amountToAllocate: number;
      if (isNegativeTransaction) {
        // Negative transactions (credit notes, credit adjustments): allocate full negative balance
        amountToAllocate = currentAllocated !== 0 ? currentAllocated : outstandingAmount;
      } else {
        amountToAllocate = currentAllocated > 0 ? currentAllocated : Math.min(outstandingAmount, availableAmount);

        if (amountToAllocate <= 0 || availableAmount <= 0) {
          toast.error('Insufficient receipt amount remaining');
          return;
        }
      }

      const newRows = [...allocationRows];
      newRows[originalIndex] = {
        ...newRows[originalIndex],
        isSelected: true,
        allocatedAmount: amountToAllocate,
      };
      setAllocationRows(newRows);
    } else {
      // When unchecking, clear the allocated amount
      const newRows = [...allocationRows];
      newRows[originalIndex] = {
        ...newRows[originalIndex],
        isSelected: false,
        allocatedAmount: 0,
      };
      setAllocationRows(newRows);
    }
  };

  // Auto-allocate promises: open the review modal instead of applying immediately.
  const handleAutoAllocatePromises = () => {
    if (!receipt) return;

    // Once data has settled, skip opening the modal when there is nothing to review.
    if (!isLoadingPromises && !promisesError && !hasOutstandingPromises(customerPromises)) {
      toast.info('No outstanding promises found for this customer');
      return;
    }

    setShowPromisesModal(true);
  };

  // Apply the user-reviewed promise allocation from the modal to the workspace rows.
  // The modal reports every transaction it manages; amount 0 means the user deselected it,
  // so those rows are cleared. Rows the modal does not manage are left untouched.
  const applyPromiseAllocations = (allocations: PromiseAllocationResult[]) => {
    const amountByTransactionId = new Map(allocations.map((item) => [item.transactionId, item.amount]));

    setAllocationRows(
      allocationRows.map((row) => {
        if (!amountByTransactionId.has(row.transaction.id)) return row;
        const amount = amountByTransactionId.get(row.transaction.id) ?? 0;
        return { ...row, isSelected: amount > 0, allocatedAmount: amount };
      }),
    );

    const applied = allocations.filter((item) => item.amount > 0);
    const allocatedTotal = applied.reduce((sum, item) => sum + item.amount, 0);
    const count = applied.length;

    if (count > 0) {
      toast.success('Promised invoices allocated', {
        description: `Allocated ${formatCurrency(allocatedTotal, displayPanelCurrency)} to ${count} invoice${count > 1 ? 's' : ''} with Promise to Pay`,
      });
    } else {
      toast.info('No promised invoices allocated');
    }
  };

  // Auto-allocate (oldest first) - only allocates invoices, preserves existing manual selections
  const handleAutoAllocate = () => {
    if (!receipt) return;

    // Calculate already-allocated amount from existing manual selections
    const existingAllocated = allocationRows.reduce((sum, row) => sum + row.allocatedAmount, 0);
    let remaining = displayOutstandingAmount - existingAllocated;

    // Sort invoices-only by due date (oldest first)
    const invoiceIndices = allocationRows
      .map((row, i) => ({ row, i }))
      .filter((x) => x.row.transaction.transactionType === 'invoice' && !x.row.isSelected)
      .sort((a, b) => new Date(a.row.transaction.dueDate).getTime() - new Date(b.row.transaction.dueDate).getTime());

    const newRows = [...allocationRows];

    for (const { row, i } of invoiceIndices) {
      if (remaining <= 0) break;

      const balance = getDisplayBalance(row);
      if (balance <= 0) continue;

      const toAllocate = Math.min(remaining, balance);
      remaining -= toAllocate;

      newRows[i] = {
        ...newRows[i],
        allocatedAmount: toAllocate,
        isSelected: true,
      };
    }

    const totalNowAllocated = newRows.reduce((sum, row) => sum + row.allocatedAmount, 0);
    const invoiceCount = newRows.filter((x) => x.isSelected && x.transaction.transactionType === 'invoice').length;

    setAllocationRows(newRows);
    toast.success('Auto-allocation complete', {
      description: `Allocated ${formatCurrency(totalNowAllocated, displayPanelCurrency)} across ${invoiceCount} invoice${invoiceCount !== 1 ? 's' : ''}`,
    });
  };

  // Clear all allocations
  const handleClearAllocation = () => {
    setAllocationRows(
      allocationRows.map((row) => ({
        ...row,
        allocatedAmount: 0,
        isSelected: false,
      })),
    );
    toast.success('Allocation cleared');
  };

  // Deselect all
  const handleDeselectAll = () => {
    setAllocationRows(
      allocationRows.map((row) => ({
        ...row,
        allocatedAmount: 0,
        isSelected: false,
      })),
    );
  };

  // Save as unapplied
  const handleSaveAsUnapplied = () => {
    toast.success('Allocation saved successfully', {
      description: 'You can return to complete this allocation later',
    });
    navigate('/payments/receipts');
  };

  // Allocate and confirm
  const handleAllocateConfirm = () => {
    const allocatedRows = allocationRows.filter((row) => row.allocatedAmount !== 0);

    if (allocatedRows.length === 0) {
      toast.error('Please allocate at least one transaction');
      return;
    }

    if (totalAllocated <= 0) {
      toast.error('Total allocated must be greater than 0');
      return;
    }

    // Reset allocation date to today when opening the dialog
    setAllocationDate(format(new Date(), 'yyyy-MM-dd'));
    setShowConfirmDialog(true);
  };

  // Confirm allocation - calls the API (respects demo/live mode)
  const confirmAllocation = async () => {
    if (!receipt) return;

    const allocatedRows = allocationRows.filter((row) => row.allocatedAmount !== 0);

    const allocationItems: AllocationItemRequest[] = allocatedRows.map((row) => ({
      transactionId: row.transaction.id,
      allocatedAmount: row.allocatedAmount,
      allocationMethod: 'Manual',
    }));

    try {
      await createAllocations({
        receiptId: receipt.id,
        allocations: allocationItems,
        allocationDate: allocationDate,
        currencyCode: displayPanelCurrency,
      });

      toast.success('Receipt allocated successfully', {
        description: `Allocated ${formatCurrency(totalAllocated)} to ${allocatedRows.length} transaction${allocatedRows.length > 1 ? 's' : ''}`,
      });

      setShowConfirmDialog(false);
      navigate('/payments/receipts');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create allocations';

      // 504 means the allocation reached Financials but the outcome is unknown - it must not be reported as a failure.
      if (err instanceof ApiError && err.statusCode === 504) {
        setShowConfirmDialog(false);
        toast.warning('Allocation outcome unknown', { description: errorMessage, duration: 15000 });
        return;
      }

      toast.error('Failed to allocate receipt', { description: errorMessage });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
          <p className="text-muted-foreground">Loading allocation data...</p>
        </div>
      </div>
    );
  }

  if (error || !receipt) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-bold">{error?.message || 'Receipt not found'}</h1>
          <Button onClick={() => navigate('/payments/receipts')}>Back to Receipts</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6 pt-6 h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <h2 className="text-3xl font-bold tracking-tight">Receipt Allocation</h2>
          <p className="text-muted-foreground">Allocate receipt to invoices</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/payments/receipts')}>
            Back to Receipts
          </Button>
          <HelpSupportButton screenId="allocation-workspace" />
        </div>
      </div>

      {/* Top Info Row — Allocation Summary | Receipt Details | Action Buttons */}
      <div className="grid gap-3 grid-cols-1 md:grid-cols-3 shrink-0">
        {/* Allocation Summary Card */}
        <Card>
          <CardHeader className="px-4 pt-1 pb-2">
            <CardTitle className="text-sm font-semibold">Allocation Summary</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-2 pt-0 space-y-1.5">
            <div className="flex justify-between items-baseline">
              <span className="text-sm text-muted-foreground">Total Allocated:</span>
              <span className="text-base font-bold text-green-600">
                {formatCurrency(totalAllocated, displayPanelCurrency)}
              </span>
            </div>
            <div className="flex justify-between items-baseline">
              <span className="text-sm text-muted-foreground">Remaining Unallocated:</span>
              <span className="text-base font-bold text-orange-600">
                {formatCurrency(remainingAmount, displayPanelCurrency)}
              </span>
            </div>
            <div className="flex justify-between items-baseline">
              <span className="text-sm text-muted-foreground">Invoices Selected:</span>
              <span className="text-base font-bold">{selectedCount}</span>
            </div>
            <div className="space-y-1">
              <Progress value={allocationProgress} accent="teal" />
              <div className="text-xs text-muted-foreground text-center">
                {allocationProgress.toFixed(0)}% allocated
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Receipt Details Card */}
        <Card>
          <CardHeader className="px-4 pt-1 pb-2">
            <CardTitle className="text-sm font-semibold">Receipt Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 text-sm px-4 pb-2 pt-0">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Customer:</span>
              <span>
                <span className="font-bold">{receipt.customerName}</span>
                {` (${receipt.customerAccountNumber})`}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Reference:</span>
              <span className="font-medium">{receipt.receiptNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Receipt Date:</span>
              <span className="font-medium">{format(new Date(receipt.receiptDate), 'dd/MM/yyyy')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Receipt Amount:</span>
              <span className="font-medium">{formatCurrency(displayReceivedAmount, displayPanelCurrency)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons Card */}
        <Card>
          <CardContent className="px-4 pt-3 pb-3 space-y-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start border-teal-400 bg-teal-50 text-teal-700 hover:bg-teal-100 hover:text-teal-800 text-xs h-8"
              onClick={handleAutoAllocatePromises}
            >
              <Wand2 className="mr-1.5 h-3.5 w-3.5 shrink-0" />
              Auto-Allocate Promises
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start border-teal-400 bg-teal-50 text-teal-700 hover:bg-teal-100 hover:text-teal-800 text-xs h-8"
              onClick={handleAutoAllocate}
            >
              <Wand2 className="mr-1.5 h-3.5 w-3.5 shrink-0" />
              Auto-Allocate (Oldest First)
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start border-teal-400 bg-teal-50 text-teal-700 hover:bg-teal-100 hover:text-teal-800 text-xs h-8"
              onClick={handleClearAllocation}
            >
              <X className="mr-1.5 h-3.5 w-3.5 shrink-0" />
              Clear Allocation
            </Button>
            <Button size="sm" className="w-full" onClick={handleAllocateConfirm}>
              Allocate & Confirm
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Full-width Transaction Table */}
      <div className="flex-1 min-h-0 flex flex-col">
        {/* Invoice Selection Table */}
        <div className="flex flex-col flex-1 min-h-0">
          {/* Sticky Toolbar */}
          <div className="sticky top-0 z-20 bg-white border border-b-0 rounded-t-xl px-4 py-3">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by invoice number..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8"
                  />
                </div>
              </div>
              {showCurrencyFilter && (
                <Select value={currencyFilter} onValueChange={setCurrencyFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Currency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="receipt">Receipt ({receipt?.currency})</SelectItem>
                    <SelectItem value="home">Home ({homeCurrency})</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <button
                onClick={handleExportToExcel}
                title="Export to Excel"
                className="text-gray-900 hover:text-gray-600 transition-colors p-1 shrink-0"
              >
                <ArrowDownToLine className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Card */}
          <Card className="rounded-t-none border-t-0 flex-1 min-h-0 flex flex-col overflow-hidden">
            <CardContent className="flex-1 min-h-0 p-0 overflow-hidden flex flex-col">
              <div className="flex-1 min-h-0 overflow-auto">
                <div className="min-w-max">
                  <table className="w-full text-sm border-separate border-spacing-0">
                    <thead className="sticky top-0 z-10 bg-white shadow-sm border-b border-gray-200">
                      <tr className="h-10">{columnOrder.map((colId) => renderColHeader(colId))}</tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredRows.length === 0 ? (
                        <tr>
                          <td colSpan={columnOrder.length} className="text-center py-8 text-sm text-muted-foreground">
                            No invoices match your filters
                          </td>
                        </tr>
                      ) : (
                        paginatedRows.map((row, index) => (
                          <tr key={row.transaction.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            {columnOrder.map((colId) => {
                              const isPinnedL = pinnedLeft.includes(colId);
                              const isPinnedR = pinnedRight.includes(colId);
                              const tdClass = cn(
                                'px-4 py-3 text-sm border-r border-gray-200 last:border-r-0',
                                isPinnedL && 'sticky left-0 z-10 bg-inherit shadow-[2px_0_4px_rgba(0,0,0,0.06)]',
                                isPinnedR && 'sticky right-0 z-10 bg-inherit shadow-[-2px_0_4px_rgba(0,0,0,0.06)]',
                              );
                              switch (colId) {
                                case 'checkbox':
                                  return (
                                    <td key="checkbox" className={tdClass}>
                                      <Checkbox
                                        checked={row.isSelected}
                                        onCheckedChange={(checked) => handleCheckboxToggle(index, checked as boolean)}
                                      />
                                    </td>
                                  );
                                case 'transactionNumber':
                                  return (
                                    <td key={colId} className={tdClass}>
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium">{row.transaction.transactionNumber}</span>
                                        {row.transaction.isPromised && (
                                          <Badge
                                            variant="outline"
                                            className="text-xs border-blue-500 bg-blue-50 text-blue-700"
                                          >
                                            P2P
                                          </Badge>
                                        )}
                                      </div>
                                    </td>
                                  );
                                case 'transactionType':
                                  return (
                                    <td key={colId} className={tdClass}>
                                      <TransactionTypeBadge type={row.transaction.transactionType as TransactionType} />
                                    </td>
                                  );
                                case 'description':
                                  return (
                                    <td key={colId} className={tdClass}>
                                      <span className="text-muted-foreground">
                                        {row.transaction.description ?? '—'}
                                      </span>
                                    </td>
                                  );
                                case 'currency':
                                  return (
                                    <td key={colId} className={tdClass}>
                                      {row.transaction.currency}
                                    </td>
                                  );
                                case 'transactionDate':
                                  return (
                                    <td key={colId} className={tdClass}>
                                      {format(new Date(row.transaction.transactionDate), 'dd/MM/yyyy')}
                                    </td>
                                  );
                                case 'dueDate':
                                  return (
                                    <td key={colId} className={tdClass}>
                                      <span
                                        className={cn(row.transaction.daysOverdue > 0 && 'text-red-600 font-medium')}
                                      >
                                        {format(new Date(row.transaction.dueDate), 'dd/MM/yyyy')}
                                      </span>
                                    </td>
                                  );
                                case 'originalAmount':
                                  return (
                                    <td key={colId} className={cn(tdClass, 'text-right')}>
                                      {currencyFilter === 'home'
                                        ? formatCurrency(
                                            row.transaction.homeCurrencyAmount ?? row.transaction.originalAmount,
                                            homeCurrency,
                                          )
                                        : formatCurrency(row.transaction.originalAmount, row.transaction.currency)}
                                    </td>
                                  );
                                case 'currentBalance':
                                  return (
                                    <td key={colId} className={cn(tdClass, 'text-right font-medium')}>
                                      {currencyFilter === 'home'
                                        ? formatCurrency(
                                            row.transaction.homeCurrencyBalance ?? row.transaction.currentBalance,
                                            homeCurrency,
                                          )
                                        : formatCurrency(row.transaction.currentBalance, row.transaction.currency)}
                                    </td>
                                  );
                                case 'allocatedAmount':
                                  return (
                                    <td key={colId} className={cn(tdClass, 'text-right')}>
                                      <CurrencyInput
                                        currencyCode={
                                          currencyFilter === 'home' ? homeCurrency : row.transaction.currency
                                        }
                                        value={row.allocatedAmount !== 0 ? row.allocatedAmount : null}
                                        onChange={(val) => handleAllocationChange(index, val)}
                                        min={getDisplayBalance(row) < 0 ? getDisplayBalance(row) : 0}
                                        max={getDisplayBalance(row) < 0 ? 0 : getDisplayBalance(row)}
                                        sign={getDisplayBalance(row) < 0 ? 'negative' : 'positive'}
                                        className="text-right"
                                        placeholder="0.00"
                                      />
                                    </td>
                                  );
                                default:
                                  return null;
                              }
                            })}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>

            {/* Pagination Footer */}
            <div className="shrink-0 flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-white pr-16">
              <span className="text-sm text-gray-500">
                Page <span className="font-semibold text-gray-900">{currentPage}</span> of{' '}
                <span className="font-semibold text-gray-900">{totalPages}</span>
              </span>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Rows per page:</span>
                  <Select
                    value={String(rowsPerPage)}
                    onValueChange={(v) => {
                      setRowsPerPage(Number(v));
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="w-[80px] h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[10, 25, 50, 100, 200].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  {(() => {
                    const pages: (number | string)[] = [];
                    if (totalPages <= 5) {
                      for (let i = 1; i <= totalPages; i++) pages.push(i);
                    } else {
                      pages.push(1);
                      if (currentPage > 3) pages.push('...');
                      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++)
                        pages.push(i);
                      if (currentPage < totalPages - 2) pages.push('...');
                      pages.push(totalPages);
                    }
                    return pages.map((p, i) =>
                      p === '...' ? (
                        <span key={`ellipsis-${i}`} className="px-1 text-gray-400 text-sm">
                          …
                        </span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setCurrentPage(p as number)}
                          className={`min-w-[32px] h-8 px-2 rounded text-sm font-medium transition-colors ${
                            currentPage === p ? 'bg-teal-600 text-white' : 'hover:bg-gray-100 text-gray-700'
                          }`}
                        >
                          {p}
                        </button>
                      ),
                    );
                  })()}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Auto-Allocate Promises review modal */}
      {receipt && (
        <AutoAllocatePromisesModal
          open={showPromisesModal}
          onOpenChange={setShowPromisesModal}
          customerName={receipt.customerName}
          customerAccountNumber={receipt.customerAccountNumber}
          promises={customerPromises}
          isLoading={isLoadingPromises}
          error={promisesError}
          onRetry={refetchPromises}
          allocationRows={allocationRows}
          getDisplayBalance={getDisplayBalance}
          receiptBalance={displayOutstandingAmount}
          displayCurrency={displayPanelCurrency}
          formatCurrency={formatCurrency}
          onConfirm={applyPromiseAllocations}
        />
      )}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Allocation</DialogTitle>
            <DialogDescription>
              Are you sure you want to allocate {formatCurrency(totalAllocated, displayPanelCurrency)} to{' '}
              {allocationRows.filter((r) => r.allocatedAmount !== 0).length} transaction
              {allocationRows.filter((r) => r.allocatedAmount !== 0).length !== 1 ? 's' : ''}?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="allocation-date">Allocation Date</Label>
              <Input
                id="allocation-date"
                type="date"
                value={allocationDate}
                onChange={(e) => setAllocationDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Receipt Amount:</span>
                <span className="font-medium">{formatCurrency(displayOutstandingAmount, displayPanelCurrency)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Allocated:</span>
                <span className="font-medium text-green-600">
                  {formatCurrency(totalAllocated, displayPanelCurrency)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Remaining:</span>
                <span className="font-medium text-orange-600">
                  {formatCurrency(remainingAmount, displayPanelCurrency)}
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={confirmAllocation} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Allocating...
                </>
              ) : (
                'Confirm Allocation'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
